// Typed-signature matching for `certification` fields.
//
// The name a user is asked to reproduce comes from the Auth0 `name` claim,
// surfaced on /session as user.name (see backend routes/sessionRouter.ts).
// That claim is stored raw — nothing trims or collapses it — so it can carry
// leading/trailing spaces or a double space from whatever was typed at
// registration. HTML collapses that whitespace when the expected name is
// rendered, so a user reading "Geoff Easton" and typing "Geoff Easton" would
// fail a byte-exact comparison against "Geoff  Easton" with no visible reason
// why. Both sides must be normalized the same way, hence one helper shared by
// the renderer (which paints the error) and the page (which gates Submit) —
// two copies of this rule are how they drift apart.

/**
 * Canonical form used only for comparison. Never persist this — the signature
 * is stored exactly as the user typed it.
 */
export const normalizeSignature = (value: unknown): string =>
  String(value ?? "")
    // Unicode spaces (NBSP from a paste, thin space from a mobile keyboard)
    // are invisible next to a plain space; fold them in before collapsing.
    .replace(/\s+/g, " ")
    .trim()
    .toLocaleLowerCase();

const EMAIL_SHAPE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * The name the user must reproduce, and which the field displays.
 *
 * Auth0 defaults `name` to the email address for users created outside the
 * registration route (dashboard, bulk import), which turns the prompt into
 * "sign your name: geoff.easton@bydem.com" — unsignable in practice. Fall
 * back to the given_name/family_name pair the session already resolves for
 * the Registration block, which is the name shown elsewhere on the profile.
 * Returns "" when the session carries no usable name at all; callers treat
 * that as "cannot verify" rather than "never matches".
 */
export const resolveSignatureName = (user: any): string => {
  const claimed = String(user?.name ?? "").replace(/\s+/g, " ").trim();
  if (claimed && !EMAIL_SHAPE.test(claimed)) {
    return claimed;
  }

  const composed = [user?.firstName, user?.lastName]
    .map((part) => String(part ?? "").replace(/\s+/g, " ").trim())
    .filter(Boolean)
    .join(" ");

  return composed || claimed;
};

/**
 * Whether a typed signature satisfies the certification field.
 *
 * Case-insensitive: the value carries legal weight through the signed record
 * (signatureMeta.typedName stores the verbatim keystrokes), not through
 * matching the capitalisation of an Auth0 profile field the user never chose
 * and often cannot edit.
 *
 * When no expected name can be resolved the field accepts any non-empty
 * signature. A session missing its name claim is our defect, and blocking a
 * user out of their own profile submission is the worse failure — the typed
 * value is still captured for the audit trail either way.
 */
export const isSignatureValid = (typed: unknown, expectedName: string): boolean => {
  const signature = normalizeSignature(typed);
  if (!signature) return false;

  const expected = normalizeSignature(expectedName);
  if (!expected) return true;

  return signature === expected;
};
